/**
 * asnOnePagerTableStyles.ts
 * Styles for AsnOnePagerTable — the ASN Gap location table rendered with the
 * KPI One Pager layout (sticky grain columns + scroll-synced metric pane).
 *
 * Adapted from onePagerTableStyles.ts with all SPARC category styling removed
 * and a wider metric column to fit ASN labels ("Non-ASN Loads", "Case Qty").
 */

import { Platform, StyleSheet } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import { MAX_VISIBLE_ROWS } from '../constants/asnOnePagerConstants';

/** Fixed row height so FlatList can use getItemLayout */
export const ROW_HEIGHT = 44;

/** Width of each grain (sticky) column */
export const GRAIN_COL_WIDTH = 140;

/** Width of each metric column — wider than the KPI One Pager to fit ASN labels */
export const METRIC_COL_WIDTH = 104;

/** Height of the metric column header row (fits two wrapped lines) */
export const COLUMN_HEADER_HEIGHT = 54;

/** Maximum height of the scrollable body area */
export const BODY_CONTAINER_HEIGHT = ROW_HEIGHT * MAX_VISIBLE_ROWS;

export const asnOnePagerTableStyles = StyleSheet.create({
  // ─── Container ────────────────────────────────────────────────────────
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    overflow: 'hidden',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },

  // ─── Section Header ───────────────────────────────────────────────────
  sectionHeader: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
  },
  sectionHeaderLeft: {
    flexShrink: 1,
    minWidth: 220,
  },
  sectionTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    color: '#2D2D2D',
  },
  sectionTitleGrain: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: '#6A6D71',
  },
  sectionHint: {
    marginTop: 4,
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: '#838383',
  },
  sectionHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  // ─── Search ───────────────────────────────────────────────────────────
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#DCE3EC',
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 32,
    minWidth: 190,
    backgroundColor: '#FFFFFF',
  },
  searchInput: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: '#2D2D2D',
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },

  // ─── Header buttons ───────────────────────────────────────────────────
  headerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    height: 32,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#034895',
    backgroundColor: '#FFFFFF',
  },
  headerButtonDisabled: {
    opacity: 0.75,
  },
  headerButtonSpinner: {
    marginRight: 2,
  },
  headerButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: '#034895',
  },

  // ─── Metric picker popover ────────────────────────────────────────────
  pickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.18)',
  },
  picker: {
    position: 'absolute',
    minWidth: 220,
    maxHeight: 340,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    paddingVertical: 8,
    shadowColor: '#0F172A',
    shadowOpacity: 0.16,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  pickerSectionTitle: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    fontFamily: FontFamily.bold,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: '#838383',
  },
  pickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  pickerItemText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: '#2D2D2D',
  },

  // ─── Table Layout ─────────────────────────────────────────────────────
  tableWrapper: {
    flexDirection: 'row',
  },
  stickyColumn: {
    zIndex: 10,
    backgroundColor: '#FFFFFF',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '2px 0px 4px rgba(0, 0, 0, 0.06)' } as any)
      : { elevation: 3 }),
  },
  scrollableArea: {
    flex: 1,
  },
  bodyContainer: {
    overflow: 'hidden',
  },
  stickyHeaderWrapper: {
    zIndex: 20,
    backgroundColor: '#FFFFFF',
    ...(Platform.OS === 'web' ? ({ position: 'sticky', top: 0 } as any) : {}),
  },

  // ─── Grain (sticky) Header ────────────────────────────────────────────
  stickyHeaderRow: {
    flexDirection: 'row',
    height: COLUMN_HEADER_HEIGHT,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    backgroundColor: '#F8FAFD',
  },
  stickyHeaderCell: {
    width: GRAIN_COL_WIDTH,
    height: COLUMN_HEADER_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    borderRightWidth: 1,
    borderRightColor: '#EBEBEB',
  },
  stickyHeaderText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    lineHeight: 14,
    color: '#3D4147',
    flexShrink: 1,
  },

  // ─── Metric Column Header ─────────────────────────────────────────────
  columnHeaderRow: {
    flexDirection: 'row',
    height: COLUMN_HEADER_HEIGHT,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    backgroundColor: '#F8FAFD',
  },
  columnHeaderCell: {
    width: METRIC_COL_WIDTH,
    height: COLUMN_HEADER_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  columnHeaderTextWrap: {
    flexShrink: 1,
    minWidth: 0,
  },
  columnHeaderText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    lineHeight: 14,
    color: '#3D4147',
    textAlign: 'center',
  },
  columnHeaderTextSorted: {
    color: '#005CBC',
  },
  sortIcon: {
    marginLeft: 2,
  },

  // ─── Data Row ─────────────────────────────────────────────────────────
  dataRow: {
    flexDirection: 'row',
    height: ROW_HEIGHT,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F2',
    backgroundColor: '#FFFFFF',
  },
  dataRowSelected: {
    backgroundColor: '#EFF5FC',
  },

  // ─── Grain (sticky) Data Cell ─────────────────────────────────────────
  stickyDataRow: {
    flexDirection: 'row',
    height: ROW_HEIGHT,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F2',
    backgroundColor: '#FFFFFF',
  },
  stickyCell: {
    width: GRAIN_COL_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    borderRightWidth: 1,
    borderRightColor: '#EBEBEB',
  },
  stickyCellText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: '#005CBC',
    textAlign: 'center',
  },
  stickyCellTextNonLink: {
    color: '#2D2D2D',
  },

  // ─── Metric Data Cell ─────────────────────────────────────────────────
  metricCell: {
    width: METRIC_COL_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  metricCellText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: '#2D2D2D',
    textAlign: 'center',
  },
  metricCellPrimary: {
    color: '#005CBC',
  },
  metricCellDanger: {
    color: '#C12C01',
    fontFamily: FontFamily.bold,
  },
  metricCellMuted: {
    color: '#838383',
  },

  // ─── Grand Total Row ──────────────────────────────────────────────────
  grandTotalRow: {
    flexDirection: 'row',
    height: ROW_HEIGHT + 4,
    borderTopWidth: 1,
    borderTopColor: '#D0D0D0',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    backgroundColor: '#F3F7FD',
  },
  grandTotalCell: {
    width: GRAIN_COL_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    borderRightWidth: 1,
    borderRightColor: '#D6E4F5',
  },
  grandTotalCellText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: '#003A7D',
    textAlign: 'center',
  },
  grandTotalMetricCell: {
    width: METRIC_COL_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  grandTotalMetricText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: '#003A7D',
    textAlign: 'center',
  },

  // ─── Loading / Empty ──────────────────────────────────────────────────
  stateContainer: {
    minHeight: 180,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    gap: 8,
  },
  stateText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: '#6B6E72',
    textAlign: 'center',
  },

  // ─── Pagination Bar ───────────────────────────────────────────────────
  paginationBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#E3E9F3',
    backgroundColor: '#FAFBFC',
  },
  paginationInfo: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: '#6B6E72',
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paginationButton: {
    width: 28,
    height: 28,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  paginationButtonDisabled: {
    backgroundColor: '#F8F9FA',
    borderColor: '#F0F0F0',
  },
  paginationPageText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: '#2D2D2D',
    minWidth: 90,
    textAlign: 'center',
  },
  paginationSizeSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  paginationSizeOption: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    backgroundColor: '#FFFFFF',
  },
  paginationSizeOptionActive: {
    borderColor: '#005CBC',
    backgroundColor: '#EFF5FC',
  },
  paginationSizeText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: '#6B6E72',
  },
  paginationSizeTextActive: {
    fontFamily: FontFamily.bold,
    color: '#005CBC',
  },
});
